import { matchJobRole, type FuncaoCandidata } from '../workforce/job-role-match';
import { acharCabecalho, CAMPO_CODIGO_DE_COMPRAS, simplificarTitulo } from '../imports/cabecalho';
import type { JobRoleId } from '../shared/ids';
import { normalizeName } from '../shared/naming';
import { itemQuantityKey } from './consolidation';
import {
  listarDisciplinas,
  ordenarAvisos,
  type AbaDaPlanilha,
  type AbaLida,
  type AvisoDaPlanilha,
  type DisciplinaListada,
  type ServicoDesativado,
  type ServicoDisponivel,
  type ServicoListado,
} from './planilha-de-indices';
import {
  emptyCatalog,
  guessItemKind,
  itemDescription,
  serviceKey,
  type CatalogDiscipline,
  type CatalogItem,
  type CatalogService,
  type ServiceCatalog,
} from './service-catalog';
import { parseNumber } from './service-sheet';
import { isSteelPurchaseService, parseSteelGauge, STEEL_PURCHASE_SERVICE } from './steel';
import type { ItemKind } from './takeoff-line';

/**
 * A planilha **por peça** (Fase 28, pedido de 09/10/2026).
 *
 * A terceira forma que o botão da Planilha de serviços aceita, ao lado da numerada (o
 * orçamento) e da plana (os índices). Uma linha por peça × serviço × material, com a
 * quantidade **pronta**:
 *
 * | ANDAR    | DISCIPLINA           | SERVIÇO                | NOME | MATERIAL      | QUANTIDADE | UNIDADE | CLASSE   |
 * | -------- | -------------------- | ---------------------- | ---- | ------------- | ---------- | ------- | -------- |
 * | FUNDAÇÃO | BLOCOS DE COROAMENTO | CORTE E DOBRA FERRAGEM | B24  | CA 50 - 8 MM  | 33,18      | KG      | MATERIAL |
 *
 * O que o usuário decidiu (ADR-178):
 *
 * - **Quantidade pronta por peça.** O número não vira índice e o desenho não o recalcula: é
 *   o aço exato daquele bloco, e uma média por m³ o perderia (RN-384).
 * - **Casar com o catálogo.** "BLOCOS DE COROAMENTO" é o BLOCO, "CORTE E DOBRA FERRAGEM" é o
 *   CORTE E DOBRA DE FERRAGEM. O que não casa vira aviso e não entra — como na plana (RN-385).
 * - **Conta já.** A peça importada conta no quantitativo antes de ser posicionada.
 * - **Reimportar atualiza as que vierem.** A peça de mesmo nome tem as quantidades trocadas;
 *   a que não veio continua, e o import avisa (RN-386).
 *
 * O aço segue a regra da casa (RN-366): só o CORTE E DOBRA **compra** aço. Na MONTAGEM e na
 * INSTALAÇÃO, o kg da planilha é a quantidade do serviço, e não item.
 */

// ── A leitura ───────────────────────────────────────────────────────────────────

/** Uma linha que virou dado. A quantidade ausente é a célula vazia — não é zero. */
export interface LinhaPorPeca {
  readonly aba: string;
  readonly linha: number;
  /** "FUNDAÇÃO" — o nome do desenho, que a tela usa para sugerir o destino. */
  readonly andar?: string;
  readonly disciplina: string;
  readonly servico: string;
  /** O nome da peça como o projeto o escreve: "B24", "AP24", "E06-A - R40". */
  readonly peca: string;
  /** A descrição do material, já sem espaço sobrando ("AJUDANTE " é "AJUDANTE"). */
  readonly material: string;
  readonly quantidade?: number;
  readonly unidade?: string;
  /** A CLASSE da planilha, traduzida. Ausente quando a célula não diz uma classe conhecida. */
  readonly classe?: ItemKind;
  /** O código de compras do material (Fase 29): é ele que dá preço ao orçamento. */
  readonly codigo?: string;
}

export interface LeituraPorPeca {
  readonly abas: readonly AbaLida[];
  readonly linhas: readonly LinhaPorPeca[];
  readonly avisos: readonly AvisoDaPlanilha[];
}

// ── O que a tipologia guarda ────────────────────────────────────────────────────

/**
 * As quantidades por peça de uma tipologia — o que vai e volta do servidor.
 *
 * Pela chave natural, como o resto do catálogo da tela: o serviço por `serviceKey`, o item por
 * `itemQuantityKey(serviceKey, itemKey)`. Dentro, o nome da peça como veio na planilha.
 */
export interface QuantidadesPorPeca {
  /** serviceKey → peça → quantidade do **serviço**, na unidade dele. */
  readonly servicos: Readonly<Record<string, Readonly<Record<string, number>>>>;
  /** itemQuantityKey → peça → quantidade do **item**. */
  readonly itens: Readonly<Record<string, Readonly<Record<string, number>>>>;
}

export function semQuantidadesPorPeca(): QuantidadesPorPeca {
  return { servicos: {}, itens: {} };
}

// ── A mescla ────────────────────────────────────────────────────────────────────

/** Um nome da planilha que casou com outro do catálogo — a tela mostra o de→para. */
export interface NomeCasado {
  readonly daPlanilha: string;
  readonly doCatalogo: string;
}

export interface ResumoPorPeca {
  /** Peças distintas, por disciplina do catálogo. */
  readonly pecasPorDisciplina: Readonly<Record<string, number>>;
  /** Linhas que viraram quantidade (as de zero e as vazias não contam). */
  readonly linhasAproveitadas: number;
  readonly itensNovos: number;
  /**
   * Os itens que ganharam o código de compras da planilha (Fase 29): o novo que nasceu com ele e
   * o que já existia sem código. É o código que dá preço ao material no orçamento.
   */
  readonly codigosNovos: number;
  /**
   * Os itens de mão de obra que a importação ligou à função cadastrada pelo nome (Fase 29) — o
   * "AJUDANTE" da planilha é o "Auxiliar de Obra" da folha, e pega o R$/h dela.
   */
  readonly funcoesLigadas: number;
  /** Só os que casaram por um nome **diferente** do escrito. */
  readonly disciplinasCasadas: readonly NomeCasado[];
  readonly servicosCasados: readonly NomeCasado[];
  /** O que a planilha escreveu e a obra não tem — disciplina ou serviço —, sem repetir. */
  readonly naoCasados: readonly string[];
  /**
   * Cada nome que ficou de fora — o que não casou e o que caiu num serviço desativado — com as
   * linhas e as **peças** que ele levou junto (22/10/2026). "1 não casou" não dizia quais peças
   * perderam o serviço, e é a peça que quem importa procura na planilha.
   */
  readonly foraDaImportacao: readonly ForaDaImportacao[];
  /** Todos os avisos — os da leitura e os da mescla —, na ordem da planilha. */
  readonly avisos: readonly AvisoDaPlanilha[];
}

/** Um nome da planilha que ficou de fora da importação, e o que ficou de fora com ele. */
export interface ForaDaImportacao {
  /** "BLOCO > MONTAGEM E INSTALAÇÃO FERRAGEM": a disciplina da obra e o serviço como escrito. */
  readonly rotulo: string;
  readonly mensagem: string;
  /** Caiu num serviço que a obra desativou — não é nome errado, é escolha da obra. */
  readonly desativado: boolean;
  readonly linhas: number;
  /** As peças, sem repetir, na ordem do nome ("B2" antes de "B10"). */
  readonly pecas: readonly string[];
}

export interface MesclaPorPeca {
  /** O catálogo **inteiro**, com os itens novos — `IMPORT_CATALOG` troca o catálogo. */
  readonly catalogo: ServiceCatalog;
  /** Só o que a planilha trouxe: a tela junta com o que a tipologia já tinha. */
  readonly quantidades: QuantidadesPorPeca;
  readonly resumo: ResumoPorPeca;
}

// ── Os cabeçalhos ───────────────────────────────────────────────────────────────

/** O texto de uma célula, para comparar com o da classe: sem acento, sem caixa, sem pontuação. */
const simplificar = simplificarTitulo;

/**
 * As colunas da planilha por peça e os títulos com que ela as escreve.
 *
 * Obrigatórias: `disciplina`, `servico`, `peca`, `material` e `quantidade`. É o **nome da peça
 * com a quantidade** que separa esta planilha da plana: o modelo de índices tem Item e Índice,
 * e nenhuma das duas colunas; a aba Estacas tem Nome, mas não tem disciplina. O andar, a
 * unidade, a classe e o código de compras são opcionais — sem eles a linha ainda diz quanto de
 * quê vai em qual peça.
 *
 * A coluna pode estar em qualquer posição, e o título, escrito de qualquer jeito que o
 * reconhecedor entenda (`acharCabecalho`, em `imports/cabecalho.ts`): "Qtd.", "Cód. compras",
 * "Nome da peça", "Classe do item".
 */
export const CABECALHO_POR_PECA = {
  andar: ['andar', 'pavimento', 'nivel'],
  disciplina: ['disciplina', 'etapa da obra'],
  servico: ['servico', 'atividade'],
  peca: ['nome', 'peca', 'nome da peca', 'elemento', 'nome do elemento'],
  material: [
    'material',
    'item',
    'insumo',
    'descricao',
    'descricao do material',
    'nome do material',
  ],
  quantidade: ['quantidade'],
  unidade: ['unidade', 'unidade de medida'],
  classe: ['classe', 'tipo', 'classe do item', 'tipo do item'],
  codigo: CAMPO_CODIGO_DE_COMPRAS,
} as const;

const OBRIGATORIAS = ['disciplina', 'servico', 'peca', 'material', 'quantidade'] as const;

function cabecalhoDaAba(rows: readonly (readonly string[])[]) {
  return acharCabecalho(rows, CABECALHO_POR_PECA, OBRIGATORIAS);
}

/**
 * A planilha tem o cabeçalho por peça? Disciplina, serviço, nome, material e quantidade.
 *
 * Basta uma aba, como na plana. A tela pergunta **esta antes** de `ehPlanilhaPlana`: o
 * cabeçalho por peça escrito com ITEM no lugar de MATERIAL, ou TIPO no lugar de CLASSE, também
 * satisfaz o detector da plana (Disciplina, Serviço, Item — ou Nome, Tipo), e o contrário não
 * acontece: o modelo de índices não tem nome de peça nem quantidade.
 */
export function ehPlanilhaPorPeca(abas: readonly AbaDaPlanilha[]): boolean {
  return abas.some((aba) => cabecalhoDaAba(aba.rows) !== undefined);
}

// ── A leitura das linhas ────────────────────────────────────────────────────────

/**
 * A CLASSE da planilha, simplificada, e o que ela é no catálogo.
 *
 * O plural entra porque a obra escreve as duas formas na mesma planilha ("FERRAMENTAS" ao lado
 * de "EQUIPAMENTO"), e "M.O." é como o orçamento abrevia mão de obra.
 *
 * **Serviço não é classe do catálogo** (`ITEM_KIND_LABEL` tem cinco, e nenhuma é serviço): a
 * perfuratriz, a retroescavadeira e o ensaio são contratados por quantidade, e o que mais se
 * parece com isso é o **insumo**. Deixar o palpite decidir seria pior: a retroescavadeira vem
 * em H, e a unidade em hora o mandaria para a mão de obra — a hora de máquina na folha de
 * pagamento.
 */
const CLASSES: ReadonlyMap<string, ItemKind> = new Map<string, ItemKind>([
  ['material', 'MATERIAL'],
  ['materiais', 'MATERIAL'],
  ['mao de obra', 'MAO_DE_OBRA'],
  ['m o', 'MAO_DE_OBRA'],
  ['mo', 'MAO_DE_OBRA'],
  ['insumo', 'INSUMO'],
  ['insumos', 'INSUMO'],
  ['ferramenta', 'FERRAMENTA'],
  ['ferramentas', 'FERRAMENTA'],
  ['equipamento', 'EQUIPAMENTO'],
  ['equipamentos', 'EQUIPAMENTO'],
  ['servico', 'INSUMO'],
  ['servicos', 'INSUMO'],
]);

/** A célula sem espaço sobrando: "AJUDANTE " é "AJUDANTE", "MARTELO  N.27" é "MARTELO N.27". */
function celula(row: readonly string[], coluna: number): string {
  return coluna < 0 ? '' : (row[coluna] ?? '').trim().replace(/\s+/g, ' ');
}

/** "uma peça", "3 peças" — o aviso conta peça, e não linha. */
function pecas(quantas: number): string {
  return quantas === 1 ? 'uma peça' : `${String(quantas)} peças`;
}

function linhas(quantas: number): string {
  return quantas === 1 ? 'uma linha' : `${String(quantas)} linhas`;
}

/**
 * Avisos agrupados: o mesmo defeito em mil linhas é **um** aviso, com quantas ele cobre e onde
 * está a primeira. A planilha por peça repete cada material em cada peça — 62 blocos, 105
 * estacas —, e um aviso por linha enterraria o que importa.
 */
class Agrupados {
  private readonly grupos = new Map<
    string,
    {
      readonly aba: string;
      readonly linha: number;
      readonly mensagem: (quantas: number, pecas: number) => string;
      quantas: number;
      readonly pecas: Set<string>;
    }
  >();

  registrar(
    chave: string,
    aba: string,
    linha: number,
    mensagem: (quantas: number, pecas: number) => string,
    peca?: string,
  ): void {
    let grupo = this.grupos.get(chave);
    if (grupo === undefined) {
      grupo = { aba, linha, mensagem, quantas: 0, pecas: new Set() };
      this.grupos.set(chave, grupo);
    }
    grupo.quantas += 1;
    if (peca !== undefined) grupo.pecas.add(peca);
  }

  avisos(): AvisoDaPlanilha[] {
    return [...this.grupos.values()].map((grupo) => ({
      aba: grupo.aba,
      linha: grupo.linha,
      mensagem: grupo.mensagem(grupo.quantas, grupo.pecas.size),
    }));
  }
}

/** O sufixo dos avisos de linha: "" para uma, "São N linhas, a primeira na linha L." para mais. */
function ondeEsta(quantas: number, primeira: number): string {
  return quantas === 1 ? '' : ` São ${linhas(quantas)}, a primeira na linha ${String(primeira)}.`;
}

/**
 * As abas da pasta, lidas como planilha por peça.
 *
 * Como `lerPlanilhaDeIndices`, nada aqui olha o catálogo: a leitura devolve o que a planilha
 * **diz** — com a célula de quantidade vazia como `undefined` e o zero como zero, porque a mescla
 * trata os dois de jeitos diferentes (RN-389) — e avisa o que ela diz torto: linha sem peça,
 * quantidade que não é número, classe que ninguém conhece.
 *
 * A aba lida sai como `INDICES` em `AbaLida`: o tipo da aba só tem os quatro da plana, e aqui
 * ele diz apenas "lida" (o contrário de `IGNORADA`).
 */
export function lerPlanilhaPorPeca(abas: readonly AbaDaPlanilha[]): LeituraPorPeca {
  const lidas: AbaLida[] = [];
  const linhasLidas: LinhaPorPeca[] = [];
  const avisos: AvisoDaPlanilha[] = [];
  const classeDe = lembrar((texto) => CLASSES.get(simplificar(texto)));

  for (const aba of abas) {
    const cabecalho = cabecalhoDaAba(aba.rows);
    if (cabecalho === undefined) {
      lidas.push({ name: aba.name, tipo: 'IGNORADA', linhas: 0 });
      avisos.push({
        aba: aba.name,
        mensagem: `A aba "${aba.name}" foi ignorada: o cabeçalho não é o da planilha por peça (Disciplina, Serviço, Nome, Material, Quantidade — em qualquer coluna).`,
      });
      continue;
    }

    const c = cabecalho.colunas;
    const agrupados = new Agrupados();
    let aproveitadas = 0;

    for (let posicao = cabecalho.linha + 1; posicao < aba.rows.length; posicao += 1) {
      const row = aba.rows[posicao]!;
      const linha = posicao + 1;

      const disciplina = celula(row, c.disciplina);
      const servico = celula(row, c.servico);
      const peca = celula(row, c.peca);
      const material = celula(row, c.material);
      const quantidadeEscrita = celula(row, c.quantidade);
      const andar = celula(row, c.andar);
      const unidade = celula(row, c.unidade);
      const classeEscrita = celula(row, c.classe);
      const codigo = celula(row, c.codigo);

      // A linha em branco no meio da planilha — ou a de total, só com o andar — passa calada.
      if ([disciplina, servico, peca, material, quantidadeEscrita].every((v) => v === '')) {
        continue;
      }
      if (peca === '') {
        agrupados.registrar(
          'sem-peca',
          aba.name,
          linha,
          (quantas) => `A linha está sem o nome da peça e não entrou.${ondeEsta(quantas, linha)}`,
        );
        continue;
      }
      if (disciplina === '' || servico === '') {
        const falta = disciplina === '' ? 'disciplina' : 'serviço';
        agrupados.registrar(
          `sem-${falta}`,
          aba.name,
          linha,
          (quantas) =>
            `A linha da peça ${peca} está sem ${falta} e não entrou.${ondeEsta(quantas, linha)}`,
        );
        continue;
      }
      if (material === '') {
        agrupados.registrar(
          `sem-material|${normalizeName(servico)}`,
          aba.name,
          linha,
          (quantas) =>
            `Uma linha de ${servico} (peça ${peca}) está sem o material e não entrou.${ondeEsta(quantas, linha)}`,
        );
        continue;
      }

      let quantidade: number | undefined;
      if (quantidadeEscrita !== '') {
        const numero = parseNumber(quantidadeEscrita);
        if (numero === undefined) {
          agrupados.registrar(
            `nao-numero|${normalizeName(servico)}|${normalizeName(material)}`,
            aba.name,
            linha,
            (quantas) =>
              `A quantidade "${quantidadeEscrita}" de ${material} em ${servico} (peça ${peca}) não é um número e não entrou.${ondeEsta(quantas, linha)}`,
          );
          continue;
        }
        if (numero < 0) {
          agrupados.registrar(
            `negativa|${normalizeName(servico)}|${normalizeName(material)}`,
            aba.name,
            linha,
            (quantas) =>
              `A quantidade de ${material} em ${servico} (peça ${peca}) é negativa (${quantidadeEscrita}) e não entrou.${ondeEsta(quantas, linha)}`,
          );
          continue;
        }
        quantidade = numero;
      }

      let classe: ItemKind | undefined;
      if (classeEscrita !== '') {
        classe = classeDe(classeEscrita);
        if (classe === undefined) {
          agrupados.registrar(
            `classe|${simplificar(classeEscrita)}`,
            aba.name,
            linha,
            (quantas) =>
              `A classe "${classeEscrita}" não é reconhecida (material, insumo, ferramenta, equipamento, mão de obra ou serviço): o item novo leva o palpite pelo nome.${ondeEsta(quantas, linha)}`,
          );
        }
      }

      linhasLidas.push({
        aba: aba.name,
        linha,
        disciplina,
        servico,
        peca,
        material,
        ...(andar === '' ? {} : { andar }),
        ...(quantidade === undefined ? {} : { quantidade }),
        ...(unidade === '' ? {} : { unidade }),
        ...(classe === undefined ? {} : { classe }),
        ...(codigo === '' ? {} : { codigo }),
      });
      aproveitadas += 1;
    }

    avisos.push(...agrupados.avisos());
    lidas.push({ name: aba.name, tipo: 'INDICES', linhas: aproveitadas });
  }

  return { abas: lidas, linhas: linhasLidas, avisos: ordenarAvisos(lidas, avisos) };
}

// ── Casar os nomes (RN-385) ─────────────────────────────────────────────────────

/** As ligações que a obra põe e tira do nome sem mudar o que ele diz. */
const LIGACOES = new Set(['de', 'da', 'do', 'das', 'dos']);

/**
 * As palavras de um nome, para casar: sem acento, sem pontuação, sem "de/da/do/das/dos", e
 * cada uma no singular — tirando **um** "s" do fim.
 *
 * O singular é tosco de propósito: "BLOCOS" vira BLOCO e "PILARES" vira PILARE. Errado em
 * português, certo para comparar — os dois lados passam pela mesma regra, então PILARES casa com
 * PILARES, e "ARRANQUE DO PILAR" **não** cai dentro de PILARES (PILAR não é PILARE). Uma regra
 * de plural de verdade faria o arranque do pilar virar o pilar da estrutura.
 */
function palavras(nome: string): readonly string[] {
  return simplificar(nome)
    .split(' ')
    .filter((palavra) => palavra !== '' && !LIGACOES.has(palavra))
    .map((palavra) =>
      palavra.length > 1 && palavra.endsWith('s') ? palavra.slice(0, -1) : palavra,
    );
}

/** A chave de comparação do nome: "CORTE E DOBRA FERRAGEM" = "CORTE E DOBRA DE FERRAGEM". */
export function chaveDoNome(nome: string): string {
  return palavras(nome).join(' ');
}

/** O que `casarNome` responde: um nome, nenhum, ou mais de um — e aí não casa. */
export type NomeEncontrado<T> =
  | { readonly tipo: 'CASOU'; readonly valor: T }
  | { readonly tipo: 'AMBIGUO'; readonly candidatos: readonly T[] }
  | { readonly tipo: 'NENHUM' };

/**
 * O nome do catálogo que a planilha quis dizer (RN-385), em três olhares, do mais ao menos
 * estrito — e cada um só vale se achar **um**:
 *
 * 1. O nome igual (`normalizeName`, que preserva o acento): dois nomes que só diferem no acento
 *    continuam sendo dois, como em todo o cadastro.
 * 2. A mesma chave (`chaveDoNome`): sem acento, sem ligação, no singular — "IMPERMEABILIZACAO"
 *    é IMPERMEABILIZAÇÃO, "MONTAGEM FERRAGEM" é MONTAGEM DE FERRAGEM.
 * 3. O nome do catálogo cujas palavras estão **todas** no da planilha: "LASTRO DE CONCRETO
 *    MAGRO" é LASTRO DE CONCRETO, "BLOCOS DE COROAMENTO" é BLOCO. Com dois candidatos não há
 *    como escolher, e escolher um seria pôr a quantidade no serviço errado sem ninguém ver.
 */
export function casarNome<T extends { readonly name: string }>(
  candidatos: readonly T[],
  escrito: string,
): NomeEncontrado<T> {
  const exatos = candidatos.filter(
    (candidato) => normalizeName(candidato.name) === normalizeName(escrito),
  );
  if (exatos.length === 1) return { tipo: 'CASOU', valor: exatos[0]! };

  const chave = chaveDoNome(escrito);
  const mesmaChave = candidatos.filter((candidato) => chaveDoNome(candidato.name) === chave);
  if (mesmaChave.length === 1) return { tipo: 'CASOU', valor: mesmaChave[0]! };
  if (mesmaChave.length > 1) return { tipo: 'AMBIGUO', candidatos: mesmaChave };

  const daPlanilha = new Set(palavras(escrito));
  const contidos = candidatos.filter((candidato) => {
    const doCatalogo = palavras(candidato.name);
    return doCatalogo.length > 0 && doCatalogo.every((palavra) => daPlanilha.has(palavra));
  });
  if (contidos.length === 1) return { tipo: 'CASOU', valor: contidos[0]! };
  if (contidos.length > 1) return { tipo: 'AMBIGUO', candidatos: contidos };
  return { tipo: 'NENHUM' };
}

// ── As unidades ─────────────────────────────────────────────────────────────────

/** As grafias da mesma unidade, já simplificadas e sem espaço. */
const APELIDOS_DE_UNIDADE: ReadonlyMap<string, string> = new Map([
  ['uni', 'un'],
  ['und', 'un'],
  ['unid', 'un'],
  ['unidade', 'un'],
  ['unidades', 'un'],
  ['kgs', 'kg'],
  ['lt', 'l'],
  ['litro', 'l'],
  ['litros', 'l'],
  ['hr', 'h'],
  ['hora', 'h'],
  ['horas', 'h'],
  ['ml', 'm'],
  ['verba', 'vb'],
]);

/**
 * A unidade para comparar: `M³`, `m³` e `m3` são uma; `UNI`, `UND` e `un` também.
 *
 * `sameUnit` (`service-quantity.ts`) resolve o expoente e a caixa, mas não o apelido: a planilha
 * do cliente escreve o cimento em "UNI", e o cadastro, em "un".
 */
function unidadeComparavel(unidade: string): string {
  const limpa = simplificar(unidade).replace(/\s/g, '');
  return APELIDOS_DE_UNIDADE.get(limpa) ?? limpa;
}

function mesmaUnidade(a: string | undefined, b: string | undefined): boolean {
  return a !== undefined && b !== undefined && unidadeComparavel(a) === unidadeComparavel(b);
}

/** A ferragem em três tempos (`stage-registry.ts`): os três contam o mesmo kg (RN-387). */
const SERVICOS_DE_FERRAGEM: ReadonlySet<string> = new Set(
  [STEEL_PURCHASE_SERVICE, 'MONTAGEM DE FERRAGEM', 'INSTALAÇÃO DE FERRAGEM'].map(normalizeName),
);

function medidoEmKg(servico: ServicoListado): boolean {
  return SERVICOS_DE_FERRAGEM.has(normalizeName(servico.name)) || mesmaUnidade(servico.unit, 'kg');
}

/** Quantos nomes de peça um aviso lista antes do "e mais N". */
const MOSTRADAS = 10;

/** Dois números da planilha que são o mesmo — `1,84` e `1.8399999999999999`. */
function mesmoNumero(a: number, b: number): boolean {
  return Math.abs(a - b) <= 1e-9 * Math.max(Math.abs(a), Math.abs(b));
}

// ── A mescla ────────────────────────────────────────────────────────────────────

/** Sem acento, para o segundo olhar no item: a planilha digitada a mão esquece o cedilha. */
function semAcento(valor: string): string {
  return normalizeName(valor).normalize('NFKD').replace(/[̀-ͯ]/g, '');
}

interface DisciplinaEmTrabalho {
  readonly original?: CatalogDiscipline;
  readonly key: string;
  readonly name: string;
  readonly services: CatalogService[];
  mexida: boolean;
}

/** O que um par disciplina × serviço escrito virou — calculado uma vez, usado em cada linha. */
type Casamento =
  | {
      readonly casou: true;
      readonly disciplina: DisciplinaListada;
      readonly servico: ServicoListado;
      /** `serviceKey` do par casado — para agrupar os avisos, e não para gravar. */
      readonly chaveDoPar: string;
      /** É o CORTE E DOBRA DE FERRAGEM, o único que compra aço (RN-366)? */
      readonly ehCorte: boolean;
      readonly emKg: boolean;
    }
  | {
      readonly casou: false;
      readonly chave: string;
      readonly rotulo: string;
      readonly mensagem: string;
      readonly desativado: boolean;
    };

/** Um serviço que alguma linha tocou, com o que a conta da quantidade dele precisa (RN-388). */
interface ServicoTocado {
  readonly chave: string;
  readonly nomeDaDisciplina: string;
  readonly emKg: boolean;
  readonly unidade?: string;
  /** Peça → item → soma, só dos itens na unidade do serviço. A peça que tocou está aqui. */
  readonly pecas: Map<string, Map<string, number>>;
  /** Peça → kg de aço deste serviço. */
  readonly aco: Map<string, number>;
}

/**
 * A função lembrada: o mesmo texto, a mesma resposta, calculada uma vez.
 *
 * A planilha por peça repete cada material, unidade e serviço em cada peça — doze mil linhas
 * com umas cem grafias —, e a caixa com localidade (`toLocaleLowerCase('pt-BR')`, dentro de
 * `normalizeName`) custa microssegundos por chamada. Sem lembrar, a mescla levava segundos.
 */
function lembrar<T>(calcular: (texto: string) => T): (texto: string) => T {
  const vistos = new Map<string, T>();
  return (texto) => {
    if (vistos.has(texto)) return vistos.get(texto) as T;
    const valor = calcular(texto);
    vistos.set(texto, valor);
    return valor;
  };
}

function somarNo<K>(mapa: Map<K, number>, chave: K, valor: number): void {
  mapa.set(chave, (mapa.get(chave) ?? 0) + valor);
}

function somarNoRegistro(
  registro: Record<string, Record<string, number>>,
  chave: string,
  peca: string,
  valor: number,
): void {
  const daChave = (registro[chave] ??= {});
  daChave[peca] = (daChave[peca] ?? 0) + valor;
}

/**
 * A planilha por peça entrando no catálogo da obra e nas quantidades da tipologia.
 *
 * As regras, na ordem em que a linha as encontra:
 *
 * 1. **O nome casa com o catálogo** (RN-385, `casarNome`). Disciplina e serviço que não casam
 *    viram **um** aviso cada — com quantas linhas cobrem — e a linha não entra: como na plana,
 *    a planilha não cria disciplina nem serviço. O de→para que precisou de outro nome vai para
 *    o resumo, para a tela mostrar o que foi entendido.
 * 2. **Zero não entra, e calado** (RN-389): é a bitola que aquela peça não usa. **Vazio não
 *    entra, e avisado** — um aviso por serviço × item, "sem quantidade em N peças": é o
 *    controle tecnológico que ainda não tem número, ou a ferramenta, que não tem nem unidade.
 * 3. **O aço** (RN-366, RN-387). No CORTE E DOBRA o aço é item, casado pela **bitola** — "CA-50
 *    - 6.3 MM" e "CA 50 - 6,3 MM" são o mesmo — e criado com ela quando falta. Em qualquer outro
 *    serviço não é item: os kg da MONTAGEM e da INSTALAÇÃO são a quantidade **do serviço**.
 * 4. **O resto é item**, casado pela descrição normalizada dentro do serviço, e criado quando
 *    não existe — com a classe da planilha, ou o palpite pelo nome, e a unidade da linha. Sem
 *    índice: a quantidade dele é a da peça, e não um consumo por m³. O item que já existe não é
 *    mexido — a não ser o aço do corte e dobra que estava sem a bitola, que a ganha.
 * 5. **A quantidade é por peça** (RN-388). A do item é a soma das linhas da peça — a mesma peça
 *    pode repetir o material, e um aviso só diz quais repetiram. A do serviço em kg é o aço dele
 *    na peça; sem aço próprio (a INSTALAÇÃO, que só tem gente), o do CORTE E DOBRA da mesma peça
 *    e disciplina. A do outro serviço é o número dos itens na unidade dele, quando todos dão o
 *    mesmo (a CONCRETAGEM: o concreto e a bomba, 1,84 m³); se não dão, o serviço fica sem número
 *    e só os itens contam.
 *
 * O catálogo devolvido é **inteiro** — `IMPORT_CATALOG` troca o catálogo —, e o serviço que só o
 * cadastro conhecia entra nele quando uma linha o usa, como em `mesclarNoCatalogo`. As
 * quantidades são **só as desta planilha**: juntar com o que a tipologia já tinha é de
 * `juntarQuantidadesPorPeca`. O nome da peça fica como foi escrito pela primeira vez — "b24"
 * depois de "B24" é a mesma peça (`nomeDaPeca`).
 */
export function mesclarPorPeca(
  catalogo: ServiceCatalog | undefined,
  disponiveis: readonly ServicoDisponivel[],
  leitura: LeituraPorPeca,
  desativados: readonly ServicoDesativado[] = [],
  /**
   * As funções permitidas em cada serviço (as da disciplina dele) — para ligar a mão de obra da
   * planilha à função cadastrada pelo nome. Ausente, a mão de obra fica pela descrição.
   */
  funcoesDoServico?: (serviceKey: string) => readonly FuncaoCandidata[],
  /**
   * A função que alguém escolheu na prévia para uma descrição de mão de obra que não casou pelo
   * nome — uma vez por descrição, para todos os serviços em que ela é permitida (13/10/2026).
   * Pela descrição normalizada (`normalizeName`).
   */
  funcoesEscolhidas: ReadonlyMap<string, JobRoleId> = new Map(),
): MesclaPorPeca {
  const base = catalogo ?? emptyCatalog();
  const conhecidas = listarDisciplinas(base, disponiveis, desativados);
  // Com os desativados dentro: é onde se olha para dizer "desativado" em vez de "não existe".
  const todas = desativados.length === 0 ? conhecidas : listarDisciplinas(base, disponiveis);

  // ── O catálogo em trabalho (o mesmo jeito de `mesclarNoCatalogo`) ─────────────

  const emTrabalho = new Map<string, DisciplinaEmTrabalho>();
  const ordem: DisciplinaEmTrabalho[] = base.disciplines.map((disciplina) => {
    const trabalho: DisciplinaEmTrabalho = {
      original: disciplina,
      key: disciplina.key,
      name: disciplina.name,
      services: [...disciplina.services],
      mexida: false,
    };
    emTrabalho.set(normalizeName(disciplina.name), trabalho);
    return trabalho;
  });

  /** A disciplina do catálogo, criada na hora quando só o cadastro da obra a conhece. */
  const garantirDisciplina = (disciplina: DisciplinaListada): DisciplinaEmTrabalho => {
    const chave = normalizeName(disciplina.name);
    let trabalho = emTrabalho.get(chave);
    if (trabalho === undefined) {
      trabalho = { key: chave, name: disciplina.name, services: [], mexida: true };
      emTrabalho.set(chave, trabalho);
      ordem.push(trabalho);
    }
    return trabalho;
  };

  const garantirServico = (
    disciplina: DisciplinaListada,
    servico: ServicoListado,
  ): { readonly trabalho: DisciplinaEmTrabalho; readonly posicao: number } => {
    const trabalho = garantirDisciplina(disciplina);
    let posicao = trabalho.services.findIndex(
      (existente) => normalizeName(existente.name) === normalizeName(servico.name),
    );
    if (posicao < 0) {
      // Mesma forma de `paraCatalogo`: o que o cadastro grava é decisão confirmada.
      trabalho.services.push({
        key: serviceKey(trabalho.name, servico.name),
        name: servico.name,
        measureBy: servico.measureBy,
        measureOrigin: 'DECIDIDO',
        items: [],
        quantityBySheet: {},
        ...(servico.appliesTo === undefined ? {} : { appliesTo: servico.appliesTo }),
        ...(servico.unit === undefined ? {} : { unit: servico.unit }),
      });
      posicao = trabalho.services.length - 1;
      trabalho.mexida = true;
    }
    return { trabalho, posicao };
  };

  // ── Casar disciplina e serviço ────────────────────────────────────────────────

  const disciplinasCasadas = new Map<string, NomeCasado>();
  const servicosCasados = new Map<string, NomeCasado>();
  const anotarCasado = (
    destino: Map<string, NomeCasado>,
    daPlanilha: string,
    doCatalogo: string,
  ): void => {
    if (normalizeName(daPlanilha) === normalizeName(doCatalogo)) return;
    destino.set(`${normalizeName(daPlanilha)}»${normalizeName(doCatalogo)}`, {
      daPlanilha,
      doCatalogo,
    });
  };

  const nomes = (lista: readonly { readonly name: string }[]): string =>
    lista.map((candidato) => candidato.name).join(', ');

  const casarAgora = (
    aba: string,
    disciplinaEscrita: string,
    servicoEscrito: string,
  ): Casamento => {
    const chaveDaDisciplina = `${aba}|${normalizeName(disciplinaEscrita)}`;
    const disciplina = casarNome(conhecidas, disciplinaEscrita);
    if (disciplina.tipo !== 'CASOU') {
      const daTodas = desativados.length === 0 ? undefined : casarNome(todas, disciplinaEscrita);
      if (daTodas?.tipo === 'CASOU') {
        return {
          casou: false,
          chave: `${aba}|desativado|${normalizeName(daTodas.valor.name)}`,
          rotulo: daTodas.valor.name,
          mensagem: `Serviço desativado: a disciplina "${daTodas.valor.name}" só tem serviços desativados nesta obra, e nada entrou nela.`,
          desativado: true,
        };
      }
      return {
        casou: false,
        chave: chaveDaDisciplina,
        rotulo: disciplinaEscrita,
        mensagem:
          disciplina.tipo === 'AMBIGUO'
            ? `Não casou: a disciplina "${disciplinaEscrita}" lembra mais de uma desta obra (${nomes(disciplina.candidatos)}), e nenhuma foi escolhida.`
            : `Não casou: a disciplina "${disciplinaEscrita}" não existe nesta obra.`,
        desativado: false,
      };
    }
    anotarCasado(disciplinasCasadas, disciplinaEscrita, disciplina.valor.name);

    const servico = casarNome(disciplina.valor.services, servicoEscrito);
    if (servico.tipo !== 'CASOU') {
      const daDisciplina =
        desativados.length === 0
          ? undefined
          : todas.find(
              (outra) => normalizeName(outra.name) === normalizeName(disciplina.valor.name),
            );
      const desativado =
        daDisciplina === undefined ? undefined : casarNome(daDisciplina.services, servicoEscrito);
      if (desativado?.tipo === 'CASOU') {
        return {
          casou: false,
          chave: `${aba}|desativado|${normalizeName(disciplina.valor.name)}|${normalizeName(desativado.valor.name)}`,
          rotulo: `${disciplina.valor.name} > ${desativado.valor.name}`,
          mensagem: `Serviço desativado: "${desativado.valor.name}" (${disciplina.valor.name}) está desativado nesta obra, e nada entrou nele.`,
          desativado: true,
        };
      }
      return {
        casou: false,
        chave: `${chaveDaDisciplina}|${normalizeName(servicoEscrito)}`,
        rotulo: `${disciplina.valor.name} > ${servicoEscrito}`,
        mensagem:
          servico.tipo === 'AMBIGUO'
            ? `Não casou: o serviço "${servicoEscrito}" lembra mais de um da disciplina "${disciplina.valor.name}" (${nomes(servico.candidatos)}), e nenhum foi escolhido.`
            : `Não casou: o serviço "${servicoEscrito}" não existe na disciplina "${disciplina.valor.name}" desta obra.`,
        desativado: false,
      };
    }
    anotarCasado(servicosCasados, servicoEscrito, servico.valor.name);
    return {
      casou: true,
      disciplina: disciplina.valor,
      servico: servico.valor,
      chaveDoPar: serviceKey(disciplina.valor.name, servico.valor.name),
      ehCorte: isSteelPurchaseService(servico.valor.name),
      emKg: medidoEmKg(servico.valor),
    };
  };

  /**
   * Doze mil linhas e umas trinta combinações escritas: o casamento é feito uma vez por par. O
   * cache é pelo texto **cru** — normalizar a cada linha custava mais que casar (a caixa com
   * localidade, `toLocaleLowerCase('pt-BR')`, é cara), e duas grafias do mesmo par só casam duas
   * vezes, com a mesma resposta.
   */
  const casamentos = new Map<string, Casamento>();
  const casar = (aba: string, disciplinaEscrita: string, servicoEscrito: string): Casamento => {
    const chave = `${aba}\u0000${disciplinaEscrita}\u0000${servicoEscrito}`;
    const visto = casamentos.get(chave);
    if (visto !== undefined) return visto;
    const novo = casarAgora(aba, disciplinaEscrita, servicoEscrito);
    casamentos.set(chave, novo);
    return novo;
  };

  // ── As linhas ─────────────────────────────────────────────────────────────────

  // O mesmo texto se repete em cada peça: o que se deriva dele é calculado uma vez.
  const bitolaDe = lembrar(parseSteelGauge);
  const normalizado = lembrar(normalizeName);
  const pecaNormal = lembrar(nomeDaPeca);
  const unidadeDe = lembrar(unidadeComparavel);
  const mesmaUnidadeAqui = (a: string | undefined, b: string | undefined): boolean =>
    a !== undefined && b !== undefined && unidadeDe(a) === unidadeDe(b);

  const ausentes = new Map<
    string,
    {
      aba: string;
      linha: number;
      rotulo: string;
      mensagem: string;
      linhas: number;
      desativado: boolean;
      pecas: Set<string>;
    }
  >();
  const avisosAgrupados = new Agrupados();
  /** O item vazio que tinha unidade em alguma peça: o aviso não diz "nem unidade". */
  const vaziasComUnidade = new Set<string>();

  /**
   * O primeiro jeito de escrever cada peça: "b24" depois de "B24" continua "B24". Sem o tipo na
   * frente: "Estaca - E79-A - R40" é a "E79-A - R40", mesmo quando é ela que vem primeiro.
   */
  const nomeEscrito = new Map<string, string>();
  const pecaDaLinha = (escrita: string): string => {
    const chave = pecaNormal(escrita);
    let nome = nomeEscrito.get(chave);
    if (nome === undefined) {
      nome = semOTipoNaFrente(escrita);
      nomeEscrito.set(chave, nome);
    }
    return nome;
  };

  /** O serviço no catálogo em trabalho, pelo par casado — a posição não muda depois de criada. */
  const noCatalogo = new Map<
    ServicoListado,
    { readonly trabalho: DisciplinaEmTrabalho; readonly posicao: number }
  >();
  /** Serviço × material escrito → chave do item: o item é procurado uma vez, e não por peça. */
  const itemDoMaterial = new Map<string, string>();

  /**
   * A peça que repete o material no mesmo serviço: as linhas **somam** (RN-388), e o import diz
   * quais foram — num aviso só. Somar é o certo quando o projeto lista o mesmo aço em duas
   * posições; é o errado quando a planilha escreveu a mesma estaca duas vezes, e só quem a fez
   * sabe qual dos dois foi.
   */
  const jaSomados = new Set<string>();
  const repetidas = new Map<string, { readonly aba: string; readonly linha: number }>();
  const anotarSoma = (chave: string, peca: string, linha: LinhaPorPeca): void => {
    const somado = `${chave}\u0000${pecaNormal(peca)}`;
    if (!jaSomados.has(somado)) {
      jaSomados.add(somado);
      return;
    }
    if (!repetidas.has(peca)) repetidas.set(peca, { aba: linha.aba, linha: linha.linha });
  };

  const tocados = new Map<string, ServicoTocado>();
  /** Disciplina → peça → kg de aço do CORTE E DOBRA: o que a INSTALAÇÃO sem aço usa. */
  const acoDoCorte = new Map<string, Map<string, number>>();
  const pecasDaDisciplina = new Map<string, Set<string>>();
  const itens: Record<string, Record<string, number>> = {};
  let linhasAproveitadas = 0;
  let itensNovos = 0;
  let codigosNovos = 0;
  let funcoesLigadas = 0;

  for (const linha of leitura.linhas) {
    const casamento = casar(linha.aba, linha.disciplina, linha.servico);
    if (!casamento.casou) {
      const atual = ausentes.get(casamento.chave);
      if (atual === undefined) {
        ausentes.set(casamento.chave, {
          aba: linha.aba,
          linha: linha.linha,
          rotulo: casamento.rotulo,
          mensagem: casamento.mensagem,
          linhas: 1,
          desativado: casamento.desativado,
          pecas: new Set([pecaDaLinha(linha.peca)]),
        });
      } else {
        atual.linhas += 1;
        atual.pecas.add(pecaDaLinha(linha.peca));
      }
      continue;
    }

    const { disciplina, servico, chaveDoPar, ehCorte, emKg } = casamento;
    const peca = pecaDaLinha(linha.peca);
    const bitola = bitolaDe(linha.material);

    // A célula vazia: o controle tecnológico sem número, a ferramenta sem nem unidade (RN-389).
    // Um aviso por serviço × item, por mais peças que repitam o buraco.
    if (linha.quantidade === undefined) {
      const chave = `vazia|${chaveDoPar}|${bitola ?? normalizado(linha.material)}`;
      if (linha.unidade !== undefined) vaziasComUnidade.add(chave);
      avisosAgrupados.registrar(
        chave,
        linha.aba,
        linha.linha,
        (_quantas, quantasPecas) =>
          `${itemDescription(linha.material)} em ${servico.name} (${disciplina.name}) está sem quantidade${vaziasComUnidade.has(chave) ? '' : ' nem unidade'} em ${pecas(quantasPecas)}, e não entrou.`,
        peca,
      );
      continue;
    }
    // Zero é a bitola que a peça não usa: não entra, e não se avisa.
    if (linha.quantidade === 0) continue;

    const ehAcoDoServico = bitola !== undefined && !ehCorte;
    if (ehAcoDoServico && !emKg) {
      avisosAgrupados.registrar(
        `aco-fora|${chaveDoPar}|${bitola}`,
        linha.aba,
        linha.linha,
        (_quantas, quantasPecas) =>
          `${itemDescription(linha.material)} tem bitola e não entra em ${servico.name} (${disciplina.name}), em ${pecas(quantasPecas)}: o aço só se compra no ${STEEL_PURCHASE_SERVICE} (RN-366), e ${servico.name} não é medido em kg.`,
        peca,
      );
      continue;
    }

    let lugar = noCatalogo.get(servico);
    if (lugar === undefined) {
      lugar = garantirServico(disciplina, servico);
      noCatalogo.set(servico, lugar);
    }
    const { trabalho, posicao } = lugar;
    const atual = trabalho.services[posicao]!;
    let tocado = tocados.get(atual.key);
    if (tocado === undefined) {
      tocado = {
        chave: atual.key,
        nomeDaDisciplina: disciplina.name,
        emKg,
        ...(servico.unit === undefined ? {} : { unidade: servico.unit }),
        pecas: new Map(),
        aco: new Map(),
      };
      tocados.set(atual.key, tocado);
    }
    const daPeca = tocado.pecas.get(peca) ?? new Map<string, number>();
    tocado.pecas.set(peca, daPeca);

    let pecasDela = pecasDaDisciplina.get(disciplina.name);
    if (pecasDela === undefined) {
      pecasDela = new Set();
      pecasDaDisciplina.set(disciplina.name, pecasDela);
    }
    pecasDela.add(pecaNormal(peca));
    linhasAproveitadas += 1;

    // O aço conta para o serviço em kg — o do corte e dobra também é item, logo abaixo.
    if (
      bitola !== undefined &&
      (linha.unidade === undefined || unidadeDe(linha.unidade) === 'kg')
    ) {
      somarNo(tocado.aco, peca, linha.quantidade);
      if (ehCorte) {
        const daDisciplina = acoDoCorte.get(disciplina.name) ?? new Map<string, number>();
        acoDoCorte.set(disciplina.name, daDisciplina);
        somarNo(daDisciplina, peca, linha.quantidade);
      }
    }
    // Na MONTAGEM e na INSTALAÇÃO o aço é a quantidade do serviço, e não item (RN-387).
    if (ehAcoDoServico) {
      anotarSoma(`aco|${atual.key}|${bitola}`, peca, linha);
      continue;
    }

    const chaveDoMaterial = `${atual.key}\u0000${linha.material}`;
    const chaveJaAchada = itemDoMaterial.get(chaveDoMaterial);
    let item =
      chaveJaAchada === undefined
        ? acharItem(atual.items, linha.material, ehCorte ? bitola : undefined, linha.codigo)
        : atual.items.find((existente) => existente.key === chaveJaAchada);
    if (item === undefined) {
      const descricao = itemDescription(linha.material);
      const kind =
        linha.classe ??
        guessItemKind({
          disciplineName: disciplina.name,
          serviceName: servico.name,
          description: descricao,
          ...(linha.unidade === undefined ? {} : { unit: linha.unidade }),
        });
      item = {
        key: normalizeName(descricao),
        description: descricao,
        quantityBySheet: {},
        kind,
        kindOrigin: linha.classe === undefined ? 'PALPITE' : 'DECIDIDO',
        ...(linha.unidade === undefined ? {} : { unit: linha.unidade }),
        ...(ehCorte && bitola !== undefined ? { steelGauge: bitola } : {}),
        ...(linha.codigo === undefined || kind === 'MAO_DE_OBRA' ? {} : { code: linha.codigo }),
      };
      trabalho.services[posicao] = { ...atual, items: [...atual.items, item] };
      trabalho.mexida = true;
      itensNovos += 1;
      if (item.code !== undefined) codigosNovos += 1;
    } else if (
      linha.codigo !== undefined &&
      item.code === undefined &&
      item.kind !== 'MAO_DE_OBRA'
    ) {
      // O item que já estava no catálogo sem código ganha o da planilha — e, com ele, o preço
      // do compras (Fase 29). Mão de obra não tem código de compras (RN-252).
      const antigo = item;
      const comCodigo: CatalogItem = {
        ...antigo,
        code: linha.codigo,
        ...(ehCorte && bitola !== undefined && antigo.steelGauge === undefined
          ? { steelGauge: bitola }
          : {}),
      };
      trabalho.services[posicao] = {
        ...atual,
        items: atual.items.map((existente) => (existente === antigo ? comCodigo : existente)),
      };
      trabalho.mexida = true;
      codigosNovos += 1;
      item = comCodigo;
    } else if (
      linha.codigo !== undefined &&
      item.code !== undefined &&
      normalizeName(item.code) !== normalizeName(linha.codigo)
    ) {
      // O código que já existia vence o que chega, como na plana e na reimportação
      // (`mergeItem`): trocá-lo é decisão de quem edita o item, e não de uma planilha.
      const existente = item.code;
      avisosAgrupados.registrar(
        `codigo-outro|${atual.key}|${item.key}`,
        linha.aba,
        linha.linha,
        () =>
          `${item!.description} em ${servico.name} veio com o código ${linha.codigo!}, mas o item já tem o ${existente}: ficou o ${existente}. Para trocar, edite o código do item.`,
      );
    } else if (ehCorte && bitola !== undefined && item.steelGauge === undefined) {
      // O aço do corte e dobra que já estava no catálogo sem a bitola: ganha-a (RN-366).
      const antigo = item;
      const comBitola: CatalogItem = { ...antigo, steelGauge: bitola };
      trabalho.services[posicao] = {
        ...atual,
        items: atual.items.map((existente) => (existente === antigo ? comBitola : existente)),
      };
      trabalho.mexida = true;
      item = comBitola;
    }

    // A mão de obra pega a função da folha pelo nome (Fase 29, D11): é a função que dá o R$/h.
    // Só entre as permitidas no serviço, e nunca a que outro item do serviço já usa — a
    // gravação recusaria a função repetida (RN-252).
    if (
      item.kind === 'MAO_DE_OBRA' &&
      item.jobRoleId === undefined &&
      funcoesDoServico !== undefined
    ) {
      const agora = trabalho.services[posicao]!;
      const usadas = new Set(
        agora.items.flatMap((outro) => (outro.jobRoleId === undefined ? [] : [outro.jobRoleId])),
      );
      const livres = funcoesDoServico(agora.key).filter((candidata) => !usadas.has(candidata.id));
      const escolhida = funcoesEscolhidas.get(normalizeName(item.description));
      const funcao =
        (escolhida === undefined
          ? undefined
          : livres.find((candidata) => candidata.id === escolhida)) ??
        matchJobRole(item.description, livres);
      if (funcao !== undefined) {
        const antigo = item;
        const comFuncao: CatalogItem = { ...antigo, jobRoleId: funcao.id };
        trabalho.services[posicao] = {
          ...agora,
          items: agora.items.map((existente) => (existente === antigo ? comFuncao : existente)),
        };
        trabalho.mexida = true;
        funcoesLigadas += 1;
        item = comFuncao;
      } else {
        const semFuncao = item;
        avisosAgrupados.registrar(
          `sem-funcao|${agora.key}|${item.key}`,
          linha.aba,
          linha.linha,
          () =>
            `${semFuncao.description} em ${servico.name} (${disciplina.name}) ficou sem função: nenhuma função permitida no serviço corresponde ao nome. Escolha a função na aba de itens para o custo da hora vir da folha.`,
        );
      }
    }
    itemDoMaterial.set(chaveDoMaterial, item.key);

    const chaveDoItem = itemQuantityKey(atual.key, item.key);
    anotarSoma(chaveDoItem, peca, linha);
    somarNoRegistro(itens, chaveDoItem, peca, linha.quantidade);
    if (mesmaUnidadeAqui(linha.unidade, tocado.unidade)) {
      somarNo(daPeca, item.key, linha.quantidade);
    }
  }

  // ── A quantidade do serviço em cada peça (RN-388) ─────────────────────────────

  const servicos: Record<string, Record<string, number>> = {};
  for (const tocado of tocados.values()) {
    for (const [peca, itensNaUnidade] of tocado.pecas) {
      let quantidade: number | undefined;
      if (tocado.emKg) {
        const proprio = tocado.aco.get(peca) ?? 0;
        quantidade = proprio > 0 ? proprio : acoDoCorte.get(tocado.nomeDaDisciplina)?.get(peca);
      } else {
        const valores = [...itensNaUnidade.values()];
        const primeiro = valores[0];
        if (primeiro !== undefined && valores.every((valor) => mesmoNumero(valor, primeiro))) {
          quantidade = primeiro;
        }
      }
      if (quantidade !== undefined && quantidade > 0) {
        somarNoRegistro(servicos, tocado.chave, peca, quantidade);
      }
    }
  }

  // ── O resumo ──────────────────────────────────────────────────────────────────

  const avisos: AvisoDaPlanilha[] = [...avisosAgrupados.avisos()];
  const [primeiraRepetida] = repetidas.values();
  if (primeiraRepetida !== undefined) {
    const nomesRepetidos = [...repetidas.keys()];
    const lista =
      nomesRepetidos.length > MOSTRADAS
        ? `${nomesRepetidos.slice(0, MOSTRADAS).join(', ')} e mais ${String(nomesRepetidos.length - MOSTRADAS)}`
        : nomesRepetidos.join(', ');
    avisos.push({
      ...primeiraRepetida,
      mensagem: `${nomesRepetidos.length === 1 ? `A peça ${lista} repete` : `${String(nomesRepetidos.length)} peças repetem`} o mesmo material no mesmo serviço${nomesRepetidos.length === 1 ? '' : ` (${lista})`}, e as linhas foram somadas. Se é a mesma peça escrita duas vezes, corrija a planilha e importe de novo: a reimportação troca a peça inteira.`,
    });
  }
  for (const ausente of ausentes.values()) {
    avisos.push({
      aba: ausente.aba,
      linha: ausente.linha,
      mensagem: `${ausente.mensagem}${ondeEsta(ausente.linhas, ausente.linha)}`,
    });
  }

  const disciplinas = ordem.map((trabalho): CatalogDiscipline => {
    if (!trabalho.mexida && trabalho.original !== undefined) return trabalho.original;
    return {
      ...(trabalho.original ?? { key: trabalho.key, name: trabalho.name }),
      services: trabalho.services,
    };
  });

  return {
    catalogo: { ...base, disciplines: disciplinas },
    quantidades: { servicos, itens },
    resumo: {
      pecasPorDisciplina: Object.fromEntries(
        [...pecasDaDisciplina].map(([nome, conjunto]) => [nome, conjunto.size]),
      ),
      linhasAproveitadas,
      itensNovos,
      codigosNovos,
      funcoesLigadas,
      disciplinasCasadas: [...disciplinasCasadas.values()],
      servicosCasados: [...servicosCasados.values()],
      naoCasados: [
        ...new Set(
          [...ausentes.values()].filter((a) => !a.desativado).map((ausente) => ausente.rotulo),
        ),
      ],
      foraDaImportacao: [...ausentes.values()].map((ausente) => ({
        rotulo: ausente.rotulo,
        mensagem: ausente.mensagem,
        desativado: ausente.desativado,
        linhas: ausente.linhas,
        pecas: [...ausente.pecas].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true })),
      })),
      avisos: ordenarAvisos(leitura.abas, [...leitura.avisos, ...avisos]),
    },
  };
}

/**
 * O item do serviço que a linha quer dizer.
 *
 * O aço do corte e dobra casa pela **bitola**: "CA-50 - 6.3 MM" e "CA 50 - 6,3 MM" são a mesma
 * barra, e o item do catálogo pode ter outra descrição (o que veio sem bitola casa pela
 * descrição, e a mescla a dá). O resto casa pela descrição normalizada — chave ou descrição, como
 * a plana — e, sem igual, pela descrição sem acento, se for uma só.
 */
function acharItem(
  items: readonly CatalogItem[],
  material: string,
  bitola: CatalogItem['steelGauge'],
  codigo?: string,
): CatalogItem | undefined {
  // O código de compras manda quando a linha tem um (Fase 29), como na plana: "TIJOLO 9 FUROS" e
  // "TIJOLO CERÂMICO 9F" com o mesmo código são o mesmo tijolo.
  if (codigo !== undefined) {
    const doCodigo = items.find(
      (item) => item.code !== undefined && normalizeName(item.code) === normalizeName(codigo),
    );
    if (doCodigo !== undefined) return doCodigo;
  }
  if (bitola !== undefined) {
    const daBitola = items.find((item) => item.steelGauge === bitola);
    if (daBitola !== undefined) return daBitola;
  }
  const chave = normalizeName(material);
  const exato = items.find(
    (item) => item.key === chave || normalizeName(item.description) === chave,
  );
  if (exato !== undefined) return exato;
  const parecidos = items.filter((item) => semAcento(item.description) === semAcento(material));
  return parecidos.length === 1 ? parecidos[0] : undefined;
}

// ── Reimportar e somar ──────────────────────────────────────────────────────────

/** Cada registro de `QuantidadesPorPeca`, peça a peça — os dois lados têm a mesma forma. */
function cadaPeca(
  quantidades: QuantidadesPorPeca,
): Iterable<readonly [chave: string, peca: string, valor: number]> {
  const lados = [quantidades.servicos, quantidades.itens];
  return lados.flatMap((lado) =>
    Object.entries(lado).flatMap(([chave, porPeca]) =>
      Object.entries(porPeca).map(([peca, valor]) => [chave, peca, valor] as const),
    ),
  );
}

/**
 * Reimportar: as peças que vieram trocam **todas** as quantidades delas; as outras ficam
 * (RN-386). Devolve também quem ficou de fora da planilha nova, para o aviso.
 *
 * "Todas" é por peça, e não por serviço: o bloco que a planilha nova traz sem o REATERRO perde
 * o reaterro antigo. Misturar as duas planilhas na mesma peça deixaria um número que nenhuma das
 * duas escreveu. Quem não veio aparece pelo primeiro nome escrito, em ordem natural — B2 antes
 * de B10.
 *
 * `apelidos` é a lembrança das peças renomeadas da tipologia (RN-458, `pieceAliasMap`): a
 * planilha que ainda escreve B06 atualiza a peça que hoje se chama B06A.
 */
export function juntarQuantidadesPorPeca(
  atual: QuantidadesPorPeca | undefined,
  planilha: QuantidadesPorPeca,
  apelidos?: Readonly<Record<string, string>>,
): { readonly quantidades: QuantidadesPorPeca; readonly naoVieram: readonly string[] } {
  const nova = comOsNomesDeHoje(planilha, apelidos);
  const vieram = pecasImportadas(nova);
  const naoVieram = new Map<string, string>();

  const juntar = (
    antigo: QuantidadesPorPeca['servicos'],
    novo: QuantidadesPorPeca['servicos'],
  ): Record<string, Record<string, number>> => {
    const saida: Record<string, Record<string, number>> = {};
    for (const [chave, porPeca] of Object.entries(antigo)) {
      for (const [peca, valor] of Object.entries(porPeca)) {
        const nome = nomeDaPeca(peca);
        if (vieram.has(nome)) continue;
        if (!naoVieram.has(nome)) naoVieram.set(nome, peca);
        (saida[chave] ??= {})[peca] = valor;
      }
    }
    for (const [chave, porPeca] of Object.entries(novo)) {
      for (const [peca, valor] of Object.entries(porPeca)) {
        (saida[chave] ??= {})[peca] = valor;
      }
    }
    return saida;
  };

  const servicos = juntar(atual?.servicos ?? {}, nova.servicos);
  const itens = juntar(atual?.itens ?? {}, nova.itens);
  return {
    quantidades: { servicos, itens },
    naoVieram: [...naoVieram.values()].sort((a, b) =>
      a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' }),
    ),
  };
}

/**
 * Os nomes da planilha traduzidos para os de hoje (RN-458).
 *
 * A peça renomeada (B06 → B06A) continua B06 na planilha, e a linha B06 atualiza B06A. A
 * exceção é a planilha que já vem com os nomes novos: se ela traz B06A também, e B06A não é o
 * nome de planilha de outra peça, então o B06 dela é outra peça, e entra com o próprio nome.
 * Sem lembrança, devolve a própria planilha.
 */
export function comOsNomesDeHoje(
  planilha: QuantidadesPorPeca,
  apelidos: Readonly<Record<string, string>> | undefined,
): QuantidadesPorPeca {
  if (apelidos === undefined || Object.keys(apelidos).length === 0) return planilha;
  const vieram = pecasImportadas(planilha);
  const traduzir = (peca: string): string => {
    const hoje = apelidos[nomeDaPeca(peca)];
    if (hoje === undefined) return peca;
    const novo = nomeDaPeca(hoje);
    return vieram.has(novo) && apelidos[novo] === undefined ? peca : hoje;
  };
  const lado = (porChave: QuantidadesPorPeca['servicos']): Record<string, Record<string, number>> =>
    Object.fromEntries(
      Object.entries(porChave).map(([chave, porPeca]) => [
        chave,
        Object.fromEntries(Object.entries(porPeca).map(([peca, valor]) => [traduzir(peca), valor])),
      ]),
    );
  return { servicos: lado(planilha.servicos), itens: lado(planilha.itens) };
}

/**
 * A peça com o nome novo em todos os serviços e itens dela (RN-457) — o que o servidor fez na
 * `piece_quantities`, refeito na mesa para a próxima gravação não devolver o nome antigo.
 */
export function renomearPecaNasQuantidades(
  quantidades: QuantidadesPorPeca,
  de: string,
  para: string,
): QuantidadesPorPeca {
  const antigo = nomeDaPeca(de);
  const lado = (porChave: QuantidadesPorPeca['servicos']): Record<string, Record<string, number>> =>
    Object.fromEntries(
      Object.entries(porChave).map(([chave, porPeca]) => [
        chave,
        Object.fromEntries(
          Object.entries(porPeca).map(([peca, valor]) => [
            nomeDaPeca(peca) === antigo ? para : peca,
            valor,
          ]),
        ),
      ]),
    );
  return { servicos: lado(quantidades.servicos), itens: lado(quantidades.itens) };
}

/** As peças de uma tipologia, pelo nome normalizado — para tirar do desenho quem veio da planilha. */
export function pecasImportadas(quantidades: QuantidadesPorPeca | undefined): ReadonlySet<string> {
  const nomes = new Set<string>();
  if (quantidades === undefined) return nomes;
  for (const [, peca] of cadaPeca(quantidades)) nomes.add(nomeDaPeca(peca));
  return nomes;
}

/**
 * O tipo escrito na frente do nome, com separador: "Estaca - ", "Bloco: ", "Arranque do pilar – ".
 * Sem separador ("Estaca E1") a palavra pode ser do nome, e fica.
 */
const TIPO_NA_FRENTE =
  /^\s*(?:estacas?|blocos?|arranques?(?:\s+d[eo]s?\s+pilar(?:es)?)?|baldrames?|vigas?\s+baldrames?)\s*[-–—:]\s*(?=\S)/i;

/**
 * O nome da peça sem o tipo na frente. A planilha da Veneza escreveu a PERFURAÇÃO como
 * "Estaca - E79-A - R40" e os outros seis serviços como "E79-A - R40": era a mesma estaca, e
 * entrava duas vezes. O tipo já sai da disciplina (`importedPieceKind`); no nome ele só separa.
 */
export function semOTipoNaFrente(nome: string): string {
  return nome.replace(TIPO_NA_FRENTE, '');
}

/** O nome da peça para comparar: sem o tipo na frente, sem espaço sobrando e sem caixa. */
export function nomeDaPeca(nome: string): string {
  return semOTipoNaFrente(nome).trim().replace(/\s+/g, ' ').toLocaleUpperCase('pt-BR');
}

/** A soma das peças, por serviço e por item — o que a conta da tipologia recebe. */
export function somarPorPeca(quantidades: QuantidadesPorPeca | undefined): {
  readonly servicos: Readonly<Record<string, number>>;
  readonly itens: Readonly<Record<string, number>>;
} {
  const somar = (lado: QuantidadesPorPeca['servicos']): Record<string, number> =>
    Object.fromEntries(
      Object.entries(lado).map(([chave, porPeca]) => [
        chave,
        Object.values(porPeca).reduce((total, valor) => total + valor, 0),
      ]),
    );
  return {
    servicos: somar(quantidades?.servicos ?? {}),
    itens: somar(quantidades?.itens ?? {}),
  };
}
